import { cloneDeep, isEmpty } from 'lodash-es';
import { SuccessResponse, Warning } from 'types/api';
import { MetricRangePayloadV3 } from 'types/api/metrics/getQueryRange';
import {
	BuilderQuery,
	DistributionData,
	MetricRangePayloadV5,
	QueryEnvelope,
	QueryRangeRequestV5,
	RawData,
	ScalarData,
	TimeSeriesData,
} from 'types/api/v5/queryRange';
import { QueryDataV3 } from 'types/api/widgets/getQuery';

const isBuilderQueryEnvelope = (
	envelope: QueryEnvelope,
): envelope is QueryEnvelope & { spec: BuilderQuery } =>
	envelope.type === 'builder_query' || envelope.type === 'builder_ai_query';

function getColName(
	col: ScalarData['columns'][number],
	legendMap: Record<string, string>,
	aggregationPerQuery: Record<string, any>,
	clickhouseQueryNames: Set<string>,
): string {
	if (col.columnType === 'group') {
		return col.name;
	}

	const aggregation =
		aggregationPerQuery?.[col.queryName]?.[col.aggregationIndex];
	const legend = legendMap[col.queryName];
	const alias = aggregation?.alias;
	const expression = aggregation?.expression || '';
	const aggregationsCount = aggregationPerQuery[col.queryName]?.length || 0;
	const isSingleAggregation = aggregationsCount === 1;

	if (aggregationsCount > 0) {
		// Single aggregation: Priority is alias > legend > expression
		if (isSingleAggregation) {
			return alias || legend || expression || col.queryName;
		}

		// Multiple aggregations: Each follows single rules BUT never shows legend
		// Priority: alias > expression (legend is ignored for multiple aggregations)
		return alias || expression || col.queryName;
	}

	// clickhouse_sql value columns carry their real SQL alias in col.name — use
	// it so each value column keeps its own header instead of collapsing onto
	// the query name. Formulas/promql use placeholder names, so they fall back
	// to legend || queryName.
	if (clickhouseQueryNames.has(col.queryName)) {
		return col.name;
	}
	return legend || col.queryName;
}

function getColId(
	col: ScalarData['columns'][number],
	aggregationPerQuery: Record<string, any>,
	clickhouseQueryNames: Set<string>,
): string {
	if (col.columnType === 'group') {
		return col.name;
	}

	// clickhouse_sql value columns are keyed by their real SQL alias so multiple
	// value columns stay unique instead of all collapsing onto the query name
	// (which would overwrite every cell in the row with the last column's value).
	if (clickhouseQueryNames.has(col.queryName)) {
		return col.name;
	}

	const aggregation =
		aggregationPerQuery?.[col.queryName]?.[col.aggregationIndex];
	const expression = aggregation?.expression || '';
	const aggregationsCount = aggregationPerQuery[col.queryName]?.length || 0;
	const isMultipleAggregations = aggregationsCount > 1;

	if (isMultipleAggregations && expression) {
		return `${col.queryName}.${expression}`;
	}

	return col.queryName;
}

/**
 * Converts V5 TimeSeriesData to legacy format
 */
function convertTimeSeriesData(
	timeSeriesData: TimeSeriesData,
	legendMap: Record<string, string>,
): QueryDataV3 {
	// Convert V5 time series format to legacy QueryDataV3 format

	// Helper function to process series data
	const processSeriesData = (
		aggregations: any[],
		seriesKey:
			| 'series'
			| 'predictedSeries'
			| 'upperBoundSeries'
			| 'lowerBoundSeries'
			| 'anomalyScores',
	): any[] =>
		aggregations?.flatMap((aggregation) => {
			const { index, alias } = aggregation;
			const seriesData = aggregation[seriesKey];

			if (!seriesData || !seriesData.length) {
				return [];
			}

			return seriesData.map((series: any) => ({
				labels: series.labels
					? Object.fromEntries(
							series.labels.map((label: any) => [label.key.name, label.value]),
						)
					: {},
				labelsArray: series.labels
					? series.labels.map((label: any) => ({ [label.key.name]: label.value }))
					: [],
				values: series.values.map((value: any) => ({
					timestamp: value.timestamp,
					value: String(value.value),
				})),
				metaData: {
					alias,
					index,
					queryName: timeSeriesData.queryName,
				},
			}));
		});

	return {
		queryName: timeSeriesData.queryName,
		legend: legendMap[timeSeriesData.queryName] || timeSeriesData.queryName,
		series: processSeriesData(timeSeriesData?.aggregations, 'series'),
		predictedSeries: processSeriesData(
			timeSeriesData?.aggregations,
			'predictedSeries',
		),
		upperBoundSeries: processSeriesData(
			timeSeriesData?.aggregations,
			'upperBoundSeries',
		),
		lowerBoundSeries: processSeriesData(
			timeSeriesData?.aggregations,
			'lowerBoundSeries',
		),
		anomalyScores: processSeriesData(
			timeSeriesData?.aggregations,
			'anomalyScores',
		),
		list: null,
	};
}

/**
 * Converts V5 ScalarData array to legacy format with table structure
 */
function convertScalarDataArrayToTable(
	scalarDataArray: ScalarData[],
	legendMap: Record<string, string>,
	aggregationPerQuery: Record<string, any>,
	clickhouseQueryNames: Set<string>,
): QueryDataV3[] {
	// If no scalar data, return empty structure

	if (!scalarDataArray || scalarDataArray.length === 0) {
		return [];
	}

	// Process each scalar data separately to maintain query separation
	return scalarDataArray?.map((scalarData) => {
		// Get query name from the first column
		const queryName = scalarData?.columns?.[0]?.queryName || '';

		if ((scalarData as any)?.aggregations?.length > 0) {
			return {
				...convertTimeSeriesData(scalarData as any, legendMap),
				table: {
					columns: [],
					rows: [],
				},
				list: null,
			};
		}

		// Collect columns for this specific query
		const columns = scalarData?.columns?.map((col) => ({
			name: getColName(col, legendMap, aggregationPerQuery, clickhouseQueryNames),
			queryName: col.queryName,
			isValueColumn: col.columnType === 'aggregation',
			id: getColId(col, aggregationPerQuery, clickhouseQueryNames),
		}));

		// Process rows for this specific query
		const rows = scalarData?.data?.map((dataRow) => {
			const rowData: Record<string, any> = {};

			scalarData?.columns?.forEach((col, colIndex) => {
				const columnName = getColName(
					col,
					legendMap,
					aggregationPerQuery,
					clickhouseQueryNames,
				);
				const columnId = getColId(col, aggregationPerQuery, clickhouseQueryNames);
				rowData[columnId || columnName] = dataRow[colIndex];
			});

			return { data: rowData };
		});

		return {
			queryName,
			legend: legendMap[queryName] || '',
			series: null,
			list: null,
			table: {
				columns,
				rows,
			},
		};
	});
}

function convertScalarWithFormatForWeb(
	scalarDataArray: ScalarData[],
	legendMap: Record<string, string>,
	aggregationPerQuery: Record<string, any>,
	clickhouseQueryNames: Set<string>,
): QueryDataV3[] {
	if (!scalarDataArray || scalarDataArray.length === 0) {
		return [];
	}

	return scalarDataArray.map((scalarData) => {
		const columns =
			scalarData.columns?.map((col) => {
				const colName = getColName(
					col,
					legendMap,
					aggregationPerQuery,
					clickhouseQueryNames,
				);

				return {
					name: colName,
					queryName: col.queryName,
					isValueColumn: col.columnType === 'aggregation',
					id: getColId(col, aggregationPerQuery, clickhouseQueryNames),
				};
			}) || [];

		const rows =
			scalarData.data?.map((dataRow) => {
				const rowData: Record<string, any> = {};
				columns?.forEach((col, colIndex) => {
					rowData[col.id || col.name] = dataRow[colIndex];
				});
				return { data: rowData };
			}) || [];

		const queryName = scalarData.columns?.[0]?.queryName || '';

		return {
			queryName,
			legend: legendMap[queryName] || queryName,
			series: null,
			list: null,
			table: {
				columns,
				rows,
			},
		};
	});
}

function extractOnlyMessageBody(body: unknown): unknown {
	const isJsonBody = body && typeof body === 'object' && !Array.isArray(body);
	if (isJsonBody) {
		const keys = Object.keys(body);
		const hasOnlyMessageKey = keys.length === 1 && keys[0] === 'message';
		if (hasOnlyMessageKey) {
			const { message } = body as { message: unknown };
			return typeof message === 'string' ? message : JSON.stringify(message);
		}
	}
	return body;
}

/**
 * Converts V5 RawData to legacy format
 */
function convertRawData(
	rawData: RawData,
	legendMap: Record<string, string>,
): QueryDataV3 {
	// Convert V5 raw format to legacy QueryDataV3 format
	return {
		queryName: rawData.queryName,
		legend: legendMap[rawData.queryName] || rawData.queryName,
		series: null,
		list: rawData.rows?.map((row) => {
			const data = {
				// Map raw data to ILog structure - spread row.data first to include all properties
				...row.data,
				date: row.timestamp,
			} as any;

			if ('body' in row.data) {
				data.body = extractOnlyMessageBody(row.data.body);
			}

			return {
				timestamp: row.timestamp,
				data,
			};
		}),
		nextCursor: rawData.nextCursor,
	};
}

/**
 * Converts V5 DistributionData to legacy format
 */
function convertDistributionData(
	distributionData: DistributionData,
	legendMap: Record<string, string>,
): any {
	// Convert V5 distribution format to legacy histogram format
	return {
		...distributionData,
		legendMap,
	};
}

/**
 * Helper function to convert V5 data based on type
 */
function convertV5DataByType(
	v5Data: any,
	legendMap: Record<string, string>,
	aggregationPerQuery: Record<string, any>,
	clickhouseQueryNames: Set<string>,
): MetricRangePayloadV3['data'] {
	switch (v5Data?.type) {
		case 'time_series': {
			const timeSeriesData = v5Data.data.results as TimeSeriesData[];
			return {
				resultType: 'time_series',
				result: timeSeriesData.map((timeSeries) =>
					convertTimeSeriesData(timeSeries, legendMap),
				),
			};
		}
		case 'scalar': {
			const scalarData = v5Data.data.results as ScalarData[];
			// For scalar data, combine all results into separate table entries
			const combinedTables = convertScalarDataArrayToTable(
				scalarData,
				legendMap,
				aggregationPerQuery,
				clickhouseQueryNames,
			);
			return {
				resultType: 'scalar',
				result: combinedTables,
			};
		}
		case 'raw': {
			const rawData = v5Data.data.results as RawData[];
			return {
				resultType: 'raw',
				result: rawData.map((raw) => convertRawData(raw, legendMap)),
			};
		}
		case 'trace': {
			const traceData = v5Data.data.results as RawData[];
			return {
				resultType: 'trace',
				result: traceData.map((trace) => convertRawData(trace, legendMap)),
			};
		}
		case 'distribution': {
			const distributionData = v5Data.data.results as DistributionData[];
			return {
				resultType: 'distribution',
				result: distributionData.map((distribution) =>
					convertDistributionData(distribution, legendMap),
				),
			};
		}
		default:
			return {
				resultType: '',
				result: [],
			};
	}
}

/**
 * Converts V5 API response to legacy format expected by frontend components
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function convertV5ResponseToLegacy(
	v5Response: SuccessResponse<MetricRangePayloadV5, QueryRangeRequestV5>,
	legendMap: Record<string, string>,
	formatForWeb?: boolean,
): SuccessResponse<MetricRangePayloadV3> & { warning?: Warning } {
	const { payload, params } = v5Response;
	const v5Data = payload?.data;

	const aggregationPerQuery =
		params?.compositeQuery?.queries?.filter(isBuilderQueryEnvelope).reduce(
			(acc, query) => {
				if (
					isBuilderQueryEnvelope(query) &&
					'aggregations' in query.spec &&
					query.spec.name
				) {
					acc[query.spec.name] = query.spec.aggregations;
				}
				return acc;
			},
			{} as Record<string, any>,
		) || {};

	// clickhouse_sql queries have no aggregation metadata; their value columns
	// are named/keyed by the real SQL alias the response carries (see getColId).
	const clickhouseQueryNames = new Set<string>(
		(params?.compositeQuery?.queries ?? [])
			.filter((query) => query.type === 'clickhouse_sql')
			.map((query) => (query.spec as { name?: string })?.name)
			.filter((name): name is string => !!name),
	);

	// If formatForWeb is true, return as-is (like existing logic)
	if (formatForWeb && v5Data?.type === 'scalar') {
		const scalarData = v5Data.data.results as ScalarData[];
		const webTables = convertScalarWithFormatForWeb(
			scalarData,
			legendMap,
			aggregationPerQuery,
			clickhouseQueryNames,
		);

		return {
			...v5Response,
			payload: {
				data: {
					resultType: 'scalar',
					result: webTables,
					warnings: v5Data?.data?.warning || [],
				},
				warning: v5Data?.warning || undefined,
				meta: v5Data?.meta,
			},
			warning: v5Data?.warning || undefined,
		};
	}

	// Convert based on V5 response type
	const convertedData = convertV5DataByType(
		v5Data,
		legendMap,
		aggregationPerQuery,
		clickhouseQueryNames,
	);

	// Create legacy-compatible response structure
	const legacyResponse: SuccessResponse<MetricRangePayloadV3> = {
		...v5Response,
		payload: {
			data: convertedData,
			warning: v5Response.payload?.data?.warning || undefined,
			meta: v5Data?.meta,
		},
	};

	// Apply legend mapping (similar to existing logic)
	if (legacyResponse.payload?.data?.result) {
		legacyResponse.payload.data.result = legacyResponse.payload.data.result.map(
			(queryData: any) => {
				const newQueryData = cloneDeep(queryData);
				newQueryData.legend = legendMap[queryData.queryName];

				// If metric names is an empty object
				if (isEmpty(queryData.metric)) {
					// If metrics list is empty && the user haven't defined a legend then add the legend equal to the name of the query.
					if (newQueryData.legend === undefined || newQueryData.legend === null) {
						newQueryData.legend = queryData.queryName;
					}
					// If name of the query and the legend if inserted is same then add the same to the metrics object.
					if (queryData.queryName === newQueryData.legend) {
						newQueryData.metric = newQueryData.metric || {};
						newQueryData.metric[queryData.queryName] = queryData.queryName;
					}
				}

				return newQueryData;
			},
		);
	}

	return legacyResponse;
}
